import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { Link } from "@tanstack/react-router";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Features } from "@/components/marketing/home/Features";
import { DataSourcesShowcase } from "@/components/marketing/home/DataSourcesShowcase";
import { CaseStudyStrip } from "@/components/marketing/home/CaseStudyStrip";
import { MobileStickyCta } from "@/components/marketing/MobileStickyCta";
import { Section } from "@/components/marketing/layout/Section";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import HomeHeroV3 from "@/components/marketing/visuals/HomeHeroV3";
import { ArchivedNotice } from "@/components/marketing/ArchivedNotice";

export const Route = createFileRoute("/archive/home")({
  component: HomePage,
});

const PRODUCT_TAGS = [
  "Knowledge Base",
  "MyApp",
  "Xero",
  "Excel",
  "SQL",
  "CSV",
  "FTP",
  "GAAP",
  "Lightspeed",
  "Cin7 (Dear)",
  "Zoho CRM",
  "PaySpace Payroll",
  "SimplePay Payroll",
];

function HomePage() {
  return (
    <MarketingLayout>
      <Helmet>
        {/*
          Archived homepage, kept for reference while parts are mined for other
          pages. Noindexed so it cannot compete with the live homepage - the two
          describe different propositions, and a prospect landing here would be
          reading something we no longer offer.

          Deliberately NO canonical: pairing noindex with a canonical pointing
          at another page sends contradictory instructions. Noindex alone is the
          clear signal, and this page is absent from the sitemap.
        */}
        <meta name="robots" content="noindex,nofollow" />
        <title>
          Larasoft - AI insights, smart tables, and automated data pipelines
        </title>
        <meta
          name="description"
          content="AI-powered knowledge bases, smart tables, and data connectors - all in one platform. Usage-based pricing, no per-user fees."
        />
        <meta
          property="og:title"
          content="Larasoft - AI insights, smart tables, and automated data pipelines"
        />
        <meta
          property="og:description"
          content="AI-powered knowledge bases, smart tables, and data connectors - all in one platform."
        />
        <meta property="og:url" content="https://larasoft.global/archive/home" />
        <meta property="og:type" content="website" />
      </Helmet>

      <ArchivedNotice
        what="home page"
        to={"/"}
        currentLabel="current home page"
      />

      {/* Heading */}
      <Section
        containerClassName="max-w-4xl"
        className="pt-10 pb-0 sm:pt-12 sm:pb-0 md:pt-16 md:pb-0"
      >
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight">
          AI insights, smart tables, and automated data pipelines - all in one
          platform.
        </h1>
      </Section>

      {/* Visual */}
      <Section
        containerClassName="max-w-5xl"
        className="pt-8 pb-0 sm:pt-8 sm:pb-0"
      >
        <HomeHeroV3 />
      </Section>

      {/* Sentinel + mobile sticky CTA appears once the hero scrolls away */}
      <MobileStickyCta />

      {/* Description, CTAs, product tags */}
      <Section
        containerClassName="relative max-w-4xl space-y-6"
        className="pt-8 pb-10 sm:pt-8 sm:pb-12"
      >
        <div className="space-y-3 text-slate-600">
          <p>
            <span className="font-semibold text-brand-blue-dark">
              Larasoft
            </span>{" "}
            helps organisations capture data, connect systems, and unlock
            insights. From{" "}
            <span className="font-semibold text-brand-blue-dark">
              AI-powered knowledge bases
            </span>{" "}
            and{" "}
            <span className="font-semibold text-brand-blue-dark">
              web-based tables with database access
            </span>{" "}
            to{" "}
            <span className="font-semibold text-brand-blue-dark">
              secure data connectors
            </span>{" "}
            that feed your reporting layer automatically.
          </p>
          <p>
            Usage-based pricing across every product - pay for data, not per
            user. No adoption barriers, no per-seat decisions.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button asChild>
            <Link to="/contact">Speak to us</Link>
          </Button>
          <Button variant="outline" asChild>
            <a href="#data-sources">Explore the platform</a>
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 text-xs text-slate-500">
          {PRODUCT_TAGS.map((name) => (
            <Badge
              key={name}
              variant="outline"
              className="border-brand-blue-pale/60 bg-white/50 text-slate-600"
            >
              {name}
            </Badge>
          ))}
        </div>
      </Section>

      <CaseStudyStrip />

      <Features />
      <DataSourcesShowcase showCta />
    </MarketingLayout>
  );
}
